'use client';

import { useFormatter, useTranslations } from 'next-intl';
import type { SearchPrediction } from '@/lib/api/search/models';
import { formatFullDate } from '@/lib/utils/dates';
import { EMPTY_VALUE } from '../core/constants';
import { useSearch } from '../core/SearchContext';
import { Highlight } from '../shared/Highlight';
import { MetaDot } from '../shared/MetaDot';
import { ResultThumb } from '../shared/ResultThumb';
import { RowFigures, RowText, SearchRow } from '../shared/SearchRow';
import { volumeFormatOptions } from '../utils/numberFormat';

const EVENT_FALLBACK_IMAGE = '/evfallback.webp';
const THUMB_SIZE = 28;

interface PredictionRowProps {
  prediction: SearchPrediction;
}

/**
 * A prediction market: its picture, title, volume and end date, and the
 * leading outcome's chance — "chance" under a single-outcome event, the
 * outcome's name under one of several.
 */
export function PredictionRow({ prediction }: PredictionRowProps) {
  const t = useTranslations('search');
  const format = useFormatter();
  const { select } = useSearch();
  const href = `/event/${prediction.slug}`;

  // Same "31-DEC-2026" as the event page header.
  const endsLabel = formatFullDate(format, prediction.endDate, '-');

  const handleSelect = () =>
    select({
      id: `predictions:${prediction.slug}`,
      kind: 'predictions',
      label: prediction.title,
      href,
    });

  const meta = (
    <>
      {prediction.volume ? (
        <>
          <span className="shrink-0">
            {t('volume', {
              amount: format.number(prediction.volume, volumeFormatOptions(prediction.volume)),
            })}
          </span>
          <MetaDot />
        </>
      ) : null}
      <span className="truncate">{t('endsOn', { date: endsLabel })}</span>
    </>
  );

  return (
    <SearchRow href={href} onSelect={handleSelect}>
      <ResultThumb
        src={prediction.image}
        fallback={EVENT_FALLBACK_IMAGE}
        width={THUMB_SIZE}
        height={THUMB_SIZE}
        className="size-7 rounded-[6px]"
      />
      <RowText title={<Highlight text={prediction.title} />} meta={meta} />
      <RowFigures
        value={prediction.chance == null ? EMPTY_VALUE : `${prediction.chance}%`}
        detail={prediction.outcome ?? t('chance')}
      />
    </SearchRow>
  );
}
